// Copyright (c) Docugami, Inc. All rights reserved.

import { INavStyles, Nav } from "@fluentui/react";
import { RouteNavItem, useGlobalNavigationItems } from "hooks/GlobalNavigationItems";
import { useState, useMemo, useEffect } from "react";
import { useSelector } from "react-redux";
import { useLocation } from "react-router";
import { AppState } from "store";

enum NavItemType {
    Route,
    Action
}

function getSelectedRouteItem(pathname: string) {
    if (pathname.startsWith('/home')) {
        return RouteNavItem.Home;
    } else if (pathname.startsWith('/my-content')) {
        return RouteNavItem.MyContent;
    }

    switch (pathname) {
        case '/':
            return RouteNavItem.Home;
        case '/understand':
            return RouteNavItem.UnderstandDocs;
        case '/create':
            return RouteNavItem.CreateDocs;
        case '/workflow':
            return RouteNavItem.AutomatedWorkflows;
        case '/chat':
            return RouteNavItem.Chat;
        default:
            return '';
    }
}

interface NavItemKey {
    type: NavItemType,
    id: string
}

export const GlobalNavigationBar = (): JSX.Element | null => {
    const location = useLocation();
    const [selectedKey, setSelectedKey] = useState<NavItemKey>({
        type: NavItemType.Route,
        id: getSelectedRouteItem(location.pathname)
    });

    const hidden = useSelector((state: AppState) => !state.display.navigationBarVisible);

    const { isExpanded, items: navLinkGroups } = useGlobalNavigationItems();

    useEffect(() => {
        if (!!selectedKey.id && selectedKey.type !== NavItemType.Route) {
            // We have a selected item that is not a path, ignore location update
            return;
        }

        const currentPathId = getSelectedRouteItem(location.pathname);
        if (currentPathId !== selectedKey.id) {
            setSelectedKey({ type: NavItemType.Route, id: currentPathId });
        }
    },
        // eslint-disable-next-line react-hooks/exhaustive-deps
        [location.pathname])

    const navStyles = useMemo((): Partial<INavStyles> => (
        {
            root: {
                display: 'flex',
                flexDirection: 'column',
                width: isExpanded ? 210 : 45,
                boxSizing: 'border-box',
                border: '1.5px solid #eee',
                overflowY: 'auto',
                overflowX: 'hidden',
                height: '100%',
                transition: 'width 0.2s ease-in-out'
            },
            groupContent: {
                marginBottom: 0,
                animation: "none",
            },
            group: {
                padding: "15px 0px",
                boxShadow: 'black 0px 8px 1px -8px',

                ":first-of-type": {
                    paddingTop: 0,
                },

                ":last-of-type": {
                    boxShadow: 'none',
                    marginTop: 'auto'
                }
            }
        }
    ), [isExpanded]);

    return hidden ? null : (
        <Nav
            selectedKey={selectedKey.id}
            ariaLabel="Global navigation bar"
            styles={navStyles}
            groups={navLinkGroups} />
    );
};
